import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import type {DibDocument} from "./types";
import type {TocEntry} from "./tocUtils";
import {buildTocTree} from "./tocUtils";
import {buildTopicUrl} from "./contentUtils";
import classes from "./TableOfContents.module.css";
import {Link} from "react-router-dom";

interface TableOfContentsProps {
    document: DibDocument;
    activeSegmentId?: string;
    clickedId?: string | null;
    onClickId?: (id: string) => void;
}

export const TableOfContents = ({document: doc, activeSegmentId, clickedId, onClickId}: TableOfContentsProps) => {
    const {t} = useTranslation();
    const entries = useMemo(() => buildTocTree(doc), [doc]);
    const {topictypeId, name, topicId} = doc;

    if (entries.length === 0) return null;

    return (
        <nav className={classes.toc} aria-label={t("Title.LabelOfContents", {defaultValue: "Table of contents"})} data-click-override={clickedId ? "" : undefined}>
            <ul className={classes.list}>
                {entries.map((entry) => (
                    <TocItem key={entry.id} entry={entry} topictypeId={topictypeId} name={name} topicId={topicId} activeSegmentId={activeSegmentId} clickedId={clickedId} onClickId={onClickId} />
                ))}
            </ul>
        </nav>
    );
};

interface TocItemProps {
    entry: TocEntry;
    topictypeId: string;
    name: string;
    topicId: string;
    activeSegmentId?: string;
    clickedId?: string | null;
    onClickId?: (id: string) => void;
}

const TocItem = ({entry, topictypeId, name, topicId, activeSegmentId, clickedId, onClickId}: TocItemProps) => {
    let href = `#${entry.id}`;
    const isActive = clickedId
        ? clickedId === entry.id
        : entry.seg === "1" && entry.id === activeSegmentId;

    let link = (
        <a href={href} className={`${classes.link}${isActive ? ` ${classes.active}` : ""}`} onClick={() => onClickId?.(entry.id)}>
            {entry.title}
        </a>
    );

    if (entry.seg === "1") {
        href = buildTopicUrl(topictypeId, name, topicId, entry.id) + `#${entry.id}`;
        link = (
            <Link to={href} className={`${classes.link}${isActive ? ` ${classes.active}` : ""}`} onClick={() => onClickId?.(entry.id)}>
                {entry.title}
            </Link>
        );
    }

    return (
        <li className={classes.item}>
            {link}
            {entry.children.length > 0 && (
                <div className={classes.collapse}>
                    <ul className={classes.children}>
                        {entry.children.map((child) => (
                            <TocItem
                                key={child.id}
                                entry={child}
                                topictypeId={topictypeId}
                                name={name}
                                topicId={topicId}
                                activeSegmentId={activeSegmentId}
                                clickedId={clickedId}
                                onClickId={onClickId}
                            />
                        ))}
                    </ul>
                </div>
            )}
        </li>
    );
};
